import { DropdownData, InputType, FormField } from '@fitogest/reactive-form-builder/interfaces';
import { environment } from '../../../../../../../environments/environment';

import {
  list,
  ReactiveCantidadFieldFactory,
  ReactiveClienteFieldFactory,
  ReactiveDropdownFieldFactory,
  ReactiveFechaFieldFactory,
  ReactiveFieldFactory,
  ReactiveHiddenFieldFactory,
  ReactiveRepeatableFieldFactory,
  type ReactiveRepeatableFieldContext,
  type ReactiveRepeatableFieldColumn,
  ReactiveTextareaFieldFactory,
} from '@fitogest/reactive-form-builder/utils';
import { ChoferModel, ObraModel } from 'src/app/models/ventas/tablas-maestras.model';
import { ClienteModel } from 'src/app/models/cliente.model';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';

const ALBARAN_MATERIAL_LINE_COUNT = 4;
const ALBARAN_NUMERIC_MATERIAL_START_LINE_INDEX = 3;

export class AlbaranesFormDefinition {

  public appName = environment.appName;
  public lastFieldChanged: string = '';

  public requestType: any;
  public model: any = {};

  public chofers: DropdownData<ChoferModel> = list();
  public clientes: DropdownData<ClienteModel> = list();
  public obras: DropdownData<ObraModel> = list();
  public maquinarias: DropdownData<MaquinariaModel> = list();
  public concepto1: DropdownData<string> = list();
  public concepto2: DropdownData<string> = list();
  public concepto3: DropdownData<string> = list();
  public concepto4: DropdownData<string> = list();


  public getMaquinariaDropdownTargets(): [
    DropdownData<MaquinariaModel>,
    DropdownData<string>,
    DropdownData<string>,
    DropdownData<string>,
    DropdownData<string>,
  ] {
    return [this.maquinarias, this.concepto1, this.concepto2, this.concepto3, this.concepto4];
  }

  private readonly treatmentMaterialColumns: readonly ReactiveRepeatableFieldColumn<any>[] = [
    {
        sourcePrefix: 'cantidad',
        label: ({ lineIndex }) => ReactiveRepeatableFieldFactory.firstLineLabel('Cantidad', lineIndex),
        createField: (context) => this.createQuantityLineField(context),
    },
    {
        sourcePrefix: 'concepto',
        label: ({ lineIndex }) => ReactiveRepeatableFieldFactory.firstLineLabel('Concepto', lineIndex),
        createField: (context) => this.createConceptLineField(context),
    },
    {
        sourcePrefix: 'precio',
        label: ({ lineIndex }) => ReactiveRepeatableFieldFactory.firstLineLabel('Precio', lineIndex),
        createField: (context) => this.createNumericLineField(context),
    },
    {
        sourcePrefix: 'total',
        label: ({ lineIndex }) => ReactiveRepeatableFieldFactory.firstLineLabel('Total', lineIndex),
        createField: (context) => this.createNumericLineField(context),
    },
  ];


  public formFieldsLugar: FormField<any>[] = [
    {
        name: 'albaran_id',
        field: 'albaran_id',
        label: 'Albarán',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: Boolean(true), isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: Boolean(true),
        order: 1
    },
    ReactiveDropdownFieldFactory.create<any>({
        name: 'chofer',
        label: 'Chofer',
        type: InputType.DROPDOWN_SEARCH,
        data: this.chofers,
        dropdownConfiguration: { valuePrimaryKey: 'id' },
        optionLabel: 'label',
        optionValue: 'value',
        order: 2,
        conditions: { rememberedOnDuplicate: true, required: true },
        extra: { field: 'chofer', required: true, values: this.chofers },
    }),
    ReactiveFechaFieldFactory.create<any>({
        order: 3,
        conditions: { rememberedOnDuplicate: true, required: true },
        extra: { field: 'fecha', required: true },
    }),
    ReactiveClienteFieldFactory.create<any>({
        name: 'cliente',
        data: this.clientes,
        optionLabel: 'label',
        optionValue: 'value',
        order: 4,
        conditions: { rememberedOnDuplicate: true, required: true },
        extra: { field: 'cliente', required: true, values: this.clientes },
    }),
    {
        name: 'direccion',
        field: 'direccion',
        label: 'Dirección',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,
        order: 5
    },
    {
        name: 'cif',
        field: 'cif',
        label: 'C.I.F.',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,
        order: 6
    },
    {
        name: 'poblacion',
        field: 'poblacion',
        label: 'Población',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,
        order: 7
    },
    {
        name: 'cp',
        field: 'cp',
        label: 'C.P.',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,
        order: 8
    },
    {
        name: 'telefono',
        field: 'telefono',
        label: 'Teléfono',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,
        order: 9
    },
    {
        name: 'correo',
        field: 'correo',
        label: 'Correo',
        input: { type: InputType.TEXT, isEditable: true },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,
        order: 10
    },
    {
        name: 'obra',
        field: 'obra',
        label: 'Obra',
        input: { type: InputType.DROPDOWN_SEARCH, isEditable: true, data: this.obras, dropdownConfiguration: { valuePrimaryKey: 'id' }, optionLabel: 'label', optionValue: 'value' },
        width: 3,
        conditions: { isRememberedOnDuplicate: true, isRequired: false, isRetained: false, hasUndoEnabled: false, isVisible: true },
        required: false,        values: this.obras,

        order: 11
    },
];

  public formFieldsTratamiento: FormField<any>[] = [
    ...this.getTratamientoMaterialFields(),
    ReactiveTextareaFieldFactory.create<any>({
        name: 'trabajos_realizados',
        label: 'Trabajos Realizados',
        order: 17,
        conditions: { rememberedOnDuplicate: true },
        textAreaConfiguration: { hasVoiceTranscription: false, canTakePhoto: false, photoField: undefined },
        extra: { field: 'trabajos_realizados', required: false },
    }),
    ReactiveTextareaFieldFactory.create<any>({
        name: 'conforme',
        label: 'Conforme',
        order: 18,
        conditions: { rememberedOnDuplicate: true },
        textAreaConfiguration: { hasVoiceTranscription: false, canTakePhoto: false, photoField: undefined },
        extra: { field: 'conforme', required: false },
    }),
    ReactiveHiddenFieldFactory.create<any>({
        name: 'image',
        isEditable: true,
        order: 19,
        conditions: { rememberedOnDuplicate: true },
        extra: { field: 'image', required: false },
    }),
    ReactiveHiddenFieldFactory.create<any>({
        name: 'contabilizado',
        isEditable: true,
        order: 20,
        conditions: { rememberedOnDuplicate: true },
        extra: { field: 'contabilizado', required: false },
    }),
    ReactiveHiddenFieldFactory.create<any>({
        name: 'enviado',
        isEditable: true,
        order: 21,
        conditions: { rememberedOnDuplicate: true },
        extra: { field: 'enviado', required: false },
    }),
];

  private getTratamientoMaterialFields(): FormField<any>[] {
    return ReactiveRepeatableFieldFactory.createFields<any>({
        lineCount: ALBARAN_MATERIAL_LINE_COUNT,
        columns: this.treatmentMaterialColumns,
    });
  }

  private createQuantityLineField(context: ReactiveRepeatableFieldContext<any>): FormField<any> {
    return ReactiveCantidadFieldFactory.create<any>({
        name: context.name,
        label: context.label,
        ...(this.isLegacyTextMaterialLine(context.lineIndex) ? { type: InputType.TEXT } : {}),
        order: context.order,
        conditions: { rememberedOnDuplicate: true },
        extra: { field: context.name, required: false },
    });
  }

  private createConceptLineField(context: ReactiveRepeatableFieldContext<any>): FormField<any> {
    const data = this.getConceptDropdownData(context.lineIndex);

    return ReactiveDropdownFieldFactory.create<any>({
        name: context.name,
        label: context.label,
        type: InputType.DROPDOWN_SEARCH,
        data,
        dropdownConfiguration: { valuePrimaryKey: 'id' },
        optionLabel: 'label',
        optionValue: 'value',
        order: context.order,
        conditions: { rememberedOnDuplicate: true },
        extra: { field: context.name, required: false, values: data },
    });
  }

  private createNumericLineField(context: ReactiveRepeatableFieldContext<any>): FormField<any> {
    return ReactiveFieldFactory.create<any>({
        name: context.name,
        label: context.label,
        type: this.resolveMaterialValueInputType(context.lineIndex),
        order: context.order,
        conditions: { rememberedOnDuplicate: true },
        extra: { field: context.name, required: false },
    });
  }

  private resolveMaterialValueInputType(lineIndex: number): InputType {
    return this.isLegacyTextMaterialLine(lineIndex) ? InputType.TEXT : InputType.NUMERIC;
  }

  private isLegacyTextMaterialLine(lineIndex: number): boolean {
    return lineIndex < ALBARAN_NUMERIC_MATERIAL_START_LINE_INDEX;
  }

  private getConceptDropdownData(lineIndex: number): DropdownData<string> {
    switch (lineIndex) {
      case 1:
        return this.concepto1;
      case 2:
        return this.concepto2;
      case 3:
        return this.concepto3;
      default:
        return this.concepto4;
    }
  }

  public handleFormValueChange(tag: string) {
    this.lastFieldChanged = tag;
  }
}